/**
 * EventSource wrapper that captures SSE events and sends them to the extension
 */

import type {
	ConnectionEvent,
	HelperConfig,
	HelperMessage,
	SSEEventData,
} from './types';
import { postMessageToExtension } from './messenger';

const VERSION = '0.1.1';

// Store the original EventSource
const OriginalEventSource = window.EventSource;

// Track wrapped EventSource instances
const activeConnections = new WeakMap<EventSource, EventSourceMetadata>();

interface EventSourceMetadata {
	url: string;
	config: Required<HelperConfig>;
}

/**
 * Create a wrapped EventSource that captures events
 */
export function createEventSourceWrapper(
	config: Required<HelperConfig>,
): typeof EventSource {
	return new Proxy(OriginalEventSource, {
		construct(target, args: [string | URL, EventSourceInit?]) {
			const [url, _init] = args;
			const urlString = url.toString();

			// Check if URL should be captured
			if (!config.urlFilter(urlString)) {
				if (config.debug) {
					console.log(
						'[GraphQL Stream Viewer Helper] URL filtered out:',
						urlString,
					);
				}
				return new target(...args);
			}

			if (config.debug) {
				console.log(
					'[GraphQL Stream Viewer Helper] Wrapping EventSource:',
					urlString,
				);
			}

			// Create the native EventSource instance
			const eventSource = new target(...args);

			// Store metadata
			activeConnections.set(eventSource, {
				url: urlString,
				config,
			});

			// Use the NATIVE addEventListener to capture ALL events
			// This works regardless of whether user code uses addEventListener or on* properties
			const nativeAddEventListener = OriginalEventSource.prototype.addEventListener.bind(eventSource);

			// Capture message events (default SSE event type)
			nativeAddEventListener('message', (event: Event) => {
				if (config.debug) {
					console.log('[GraphQL Stream Viewer Helper] Message event captured:', event);
				}
				captureEvent(eventSource, 'message', event);
			});

			// Capture GraphQL Yoga SSE events (uses 'next' event type)
			nativeAddEventListener('next', (event: Event) => {
				if (config.debug) {
					console.log('[GraphQL Stream Viewer Helper] Next event captured:', event);
				}
				captureEvent(eventSource, 'next', event);
			});

			// Capture 'complete' events (GraphQL subscription completion)
			nativeAddEventListener('complete', (event: Event) => {
				if (config.debug) {
					console.log('[GraphQL Stream Viewer Helper] Complete event captured');
				}
				captureEvent(eventSource, 'complete', event);
			});

			// Capture open events
			nativeAddEventListener('open', (_event: Event) => {
				if (config.debug) {
					console.log('[GraphQL Stream Viewer Helper] Open event captured');
				}
				sendConnectionEvent(eventSource, 'open', urlString);
			});

			// Capture error events
			nativeAddEventListener('error', (event: Event) => {
				if (config.debug) {
					console.log('[GraphQL Stream Viewer Helper] Error event captured');
				}
				sendConnectionEvent(eventSource, 'error', urlString, event);
			});

			// Wrap the close method to send close event
			const originalClose = eventSource.close.bind(eventSource);
			eventSource.close = function () {
				if (config.debug) {
					console.log('[GraphQL Stream Viewer Helper] Close called');
				}
				sendConnectionEvent(eventSource, 'close', urlString);
				activeConnections.delete(eventSource);
				originalClose();
			};

			return eventSource;
		},
	}) as typeof EventSource;
}

/**
 * Capture an SSE event and send to extension
 */
function captureEvent(
	eventSource: EventSource,
	type: string,
	event: Event,
): void {
	const metadata = activeConnections.get(eventSource);
	if (!metadata) {
		return;
	}

	const messageEvent = event as MessageEvent;
	const eventData: SSEEventData = {
		type,
		data: messageEvent.data as string,
		id: messageEvent.lastEventId || undefined,
		timestamp: Date.now(),
		url: metadata.url,
		lastEventId: (eventSource as any).lastEventId || messageEvent.lastEventId,
		readyState: eventSource.readyState,
	};

	const message: HelperMessage = {
		type: 'GRAPHQL_SSE_EVENT',
		data: eventData,
		version: VERSION,
		source: 'graphql-stream-viewer-helper',
	};

	postMessageToExtension(message, metadata.config.debug);
}

/**
 * Send a connection lifecycle event to extension
 */
function sendConnectionEvent(
	eventSource: EventSource,
	type: 'open' | 'close' | 'error',
	url: string,
	errorEvent?: Event,
): void {
	const metadata = activeConnections.get(eventSource);
	if (!metadata) {
		return;
	}

	const connectionEvent: ConnectionEvent = {
		type,
		url,
		timestamp: Date.now(),
	};

	if (type === 'error' && errorEvent) {
		connectionEvent.error = {
			message: errorEvent.type || 'Unknown error',
			stack: (errorEvent as ErrorEvent).error?.stack,
		};
	}

	const message: HelperMessage = {
		type: 'GRAPHQL_SSE_CONNECTION',
		data: connectionEvent,
		version: VERSION,
		source: 'graphql-stream-viewer-helper',
	};

	postMessageToExtension(message, metadata.config.debug);
}

/**
 * Install the EventSource wrapper globally
 */
export function installEventSourceWrapper(
	config: Required<HelperConfig>,
): void {
	if (window.EventSource === OriginalEventSource) {
		window.EventSource = createEventSourceWrapper(config);

		if (config.debug) {
			console.log(
				'[GraphQL Stream Viewer Helper] EventSource wrapper installed',
			);
		}
	}
}

/**
 * Restore the original EventSource
 */
export function uninstallEventSourceWrapper(): void {
	if (window.EventSource !== OriginalEventSource) {
		window.EventSource = OriginalEventSource;
		console.log(
			'[GraphQL Stream Viewer Helper] EventSource wrapper uninstalled',
		);
	}
}
